import type { ChipProps } from "@mui/material/Chip";

export function formatCents(cents: number, currency = "GBP"): string {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency,
  }).format(cents / 100);
}

export function formatDuration(mins: number): string {
  if (mins < 60) return `${mins}min`;
  const hours = Math.floor(mins / 60);
  const remaining = mins % 60;
  if (remaining === 0) return `${hours}h`;
  return `${hours}h ${remaining}min`;
}

export function formatTime(timeStr: string): string {
  const [hours, minutes] = timeStr.split(":").map(Number);
  const period = hours >= 12 ? "PM" : "AM";
  const displayHour = hours % 12 || 12;
  return `${displayHour}:${minutes.toString().padStart(2, "0")} ${period}`;
}

export function formatDateTime(date: Date | string): string {
  const d = new Date(date);
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function formatDateOnly(date: Date | string): string {
  const d = new Date(date);
  return d.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export const DAYS_OF_WEEK = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

/** Preset category labels for offerings (dropdown + defaults). */
export const SERVICE_CATEGORIES = [
  "Body",
  "Brows",
  "Colour",
  "Consultation",
  "Fitness",
  "Hair",
  "Lashes",
  "Makeup",
  "Massage",
  "Nails",
  "Skin",
  "Waxing",
  "Wellness",
  "Other",
  "General",
];

export function getInitials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export type BookingMode = "appointment" | "resource" | "class" | "ticket";

export const BOOKING_MODE_OPTIONS: { value: BookingMode; label: string }[] = [
  { value: "appointment", label: "Appointment" },
  { value: "resource", label: "Table / Resource Booking" },
  { value: "class", label: "Class / Event" },
  { value: "ticket", label: "Ticket / Seat Booking" },
];

export const BUSINESS_TYPE_OPTIONS = [
  { value: "general", label: "General" },
  { value: "hair", label: "Hair Salon" },
  { value: "barber", label: "Barbershop" },
  { value: "beauty", label: "Beauty Studio" },
  { value: "nails", label: "Nail Salon" },
  { value: "spa", label: "Spa" },
  { value: "wellness", label: "Wellness & Therapy" },
  { value: "fitness", label: "Fitness & Personal Training" },
  { value: "tattoo", label: "Tattoo & Piercing" },
  { value: "medical", label: "Medical & Aesthetic Clinic" },
  { value: "restaurant", label: "Restaurant / Café" },
  { value: "hotel", label: "Hotel / Accommodation" },
  { value: "venue", label: "Meeting Room / Venue Hire" },
  { value: "sports", label: "Sports Facility / Activity Centre" },
  { value: "classes", label: "Classes & Events" },
  { value: "tours", label: "Tours & Experiences" },
  { value: "other", label: "Other" },
];

/** MUI Chip `color` for booking status */
export function getStatusChipColor(status: string): ChipProps["color"] {
  switch (status.toLowerCase()) {
    case "confirmed":
      return "success";
    case "pending":
    case "pending_payment":
      return "warning";
    case "cancelled":
      return "error";
    case "completed":
      return "info";
    case "no_show":
      return "default";
    default:
      return "default";
  }
}

/** Compare entity ids across JSON (numbers) and UI state (strings from selects, routes, query params). */
export function sameEntityId(a: unknown, b: unknown): boolean {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  return String(a) === String(b);
}
